/**
 * `markdownToDocument` — the direction §5.1 says is the reason the round trip
 * has to be lossless *both* ways.
 *
 * "Agents writing back parse markdown → JSON, so round-tripping must be
 * lossless in both directions" (§5.1). §11.3 lets Claude create and edit
 * drafts, and every one of those writes lands here. A parser that quietly
 * dropped a block would not look like a bug; it would look like the agent
 * having written less than it did.
 *
 * The reader is deliberately more tolerant than the writer. Canonical markdown
 * — what `documentToMarkdown` emits — round-trips byte for byte, and anything
 * else reasonable an agent writes (no block ids, `*` bullets, `1)` numbering,
 * aligned table delimiters, loose lists) normalises to the same tree. What it
 * will not do is invent a component: an unknown directive is an error, because
 * the closed set is the invariant everything downstream leans on.
 *
 * **Block ids are read, never minted, here.** Markdown an agent wrote has none,
 * and a parse that silently assigned them would be indistinguishable from a
 * parse that read them — which matters, because reassigning an id strands every
 * comment anchored to it. Assigning is `ensureBlockIds`, and the one write path
 * that composes the two is `deriveDocumentContent`.
 */

import { parseAttributes, readLoneMarker, splitTrailingMarker } from './attributes.ts';
import { normalizeDocument } from './schema.ts';
import { type DocumentJSON, MarkdownParseError, type MarkJSON, type NodeJSON } from './types.ts';

/**
 * Stands in for a hard break while a paragraph's lines are one string, so the
 * inline scanner sees the paragraph as the serializer wrote it. Stripped from
 * the input first, so it can never arrive as content.
 */
const HARD_BREAK = '\u0000';

const FENCE = /^(`{3,})(.*)$/;
const CONTAINER_DIRECTIVE = /^:::([a-z][a-z0-9-]*)(\{.*\})?$/;
const LEAF_DIRECTIVE = /^::([a-z][a-z0-9-]*)(\{.*\})?$/;
const HEADING = /^(#{1,6})(?:[ \t]+(.*))?$/;
const BLOCKQUOTE = /^>[ \t]?/;
const THEMATIC_BREAK = /^(?:-{3,}|\*{3,}|_{3,})[ \t]*$/;
const LIST_MARKER = /^([-+*]|\d{1,9}[.)])(?:[ \t]+|$)/;
const TASK_MARKER = /^\[([ xX])\][ \t]+/;
const IMAGE_LINE =
  /^!\[((?:\\.|[^\]\\])*)\]\([ \t]*(<(?:\\.|[^>\\])*>|[^\s()]*)(?:[ \t]+"((?:\\.|[^"\\])*)")?[ \t]*\)$/;

/** Directive name in markdown, node type in the schema. */
const DIRECTIVE_NODES: Readonly<Record<string, string>> = {
  callout: 'callout',
  embed: 'embed',
  'live-data': 'liveData',
};

export function markdownToDocument(markdown: string): DocumentJSON {
  const lines = markdown.replaceAll(HARD_BREAK, '').replace(/\r\n?/g, '\n').split('\n');
  const blocks = parseBlocks(lines);
  return normalizeDocument({
    type: 'doc',
    content: blocks.length > 0 ? blocks : [{ type: 'paragraph' }],
  });
}

// ---------------------------------------------------------------------------
// Blocks
// ---------------------------------------------------------------------------

function parseBlocks(lines: string[]): NodeJSON[] {
  const out: NodeJSON[] = [];
  let i = 0;

  while (i < lines.length) {
    const raw = lines[i] as string;
    const line = raw.trimStart();

    if (line === '') {
      i += 1;
      continue;
    }

    const fence = openingFence(line);
    if (fence) {
      const indent = raw.length - line.length;
      const [node, next] = readCodeBlock(lines, i, fence.length, fence.info, indent);
      out.push(node);
      i = next;
      continue;
    }

    const container = CONTAINER_DIRECTIVE.exec(line);
    if (container) {
      const [node, next] = readContainerDirective(lines, i, container[1] as string, container[2]);
      out.push(node);
      i = next;
      continue;
    }

    const leaf = LEAF_DIRECTIVE.exec(line);
    if (leaf) {
      out.push(readLeafDirective(leaf[1] as string, leaf[2]));
      i += 1;
      continue;
    }

    const heading = HEADING.exec(line);
    if (heading) {
      const { text, id } = splitTrailingMarker(heading[2] ?? '');
      out.push(
        withId({ type: 'heading', attrs: { level: (heading[1] as string).length } }, id, {
          content: parseInline(text),
        }),
      );
      i += 1;
      continue;
    }

    if (BLOCKQUOTE.test(line)) {
      const quoted: string[] = [];
      while (i < lines.length) {
        const current = (lines[i] as string).trimStart();
        if (!current.startsWith('>')) break;
        quoted.push(current.replace(BLOCKQUOTE, ''));
        i += 1;
      }
      out.push({ type: 'blockquote', content: parseBlocks(quoted) });
      continue;
    }

    if (THEMATIC_BREAK.test(line)) {
      out.push({ type: 'horizontalRule' });
      i += 1;
      continue;
    }

    if (LIST_MARKER.test(line)) {
      const [node, next] = readList(lines, i);
      out.push(node);
      i = next;
      continue;
    }

    if (line.startsWith('|') && isDelimiterRow(lines[i + 1])) {
      const [node, next] = readTable(lines, i);
      out.push(node);
      i = next;
      continue;
    }

    const withoutMarker = splitTrailingMarker(line);
    const image = IMAGE_LINE.exec(withoutMarker.text);
    if (image) {
      out.push(
        withId(
          {
            type: 'image',
            attrs: {
              src: readDestination(image[2] as string) || null,
              alt: unescapeText(image[1] as string) || null,
              title: image[3] === undefined ? null : unescapeText(image[3]),
            },
          },
          withoutMarker.id,
        ),
      );
      i += 1;
      continue;
    }

    const lone = readLoneMarker(line);
    if (lone) {
      out.push(withId({ type: 'paragraph' }, lone.id));
      i += 1;
      continue;
    }

    const [node, next] = readParagraph(lines, i);
    out.push(node);
    i = next;
  }

  return out;
}

function readParagraph(lines: string[], start: number): [NodeJSON, number] {
  const collected: string[] = [];
  let i = start;
  let endsWithHardBreak = false;

  while (i < lines.length) {
    const line = (lines[i] as string).trimStart();
    if (line === '') break;
    // A lone marker on the line after a hard break is this paragraph's own
    // marker — the only place `documentToMarkdown` can put one when the
    // paragraph ends with the break. The loop stops either way; the block after
    // it decides which of the two happened.
    if (i > start && startsBlock(line, lines[i + 1])) break;
    endsWithHardBreak = trailingBackslashes(line) % 2 === 1;
    collected.push(line);
    i += 1;
  }

  let id: string | null = null;
  if (endsWithHardBreak && i < lines.length) {
    const marker = readLoneMarker((lines[i] as string).trimStart());
    if (marker) {
      id = marker.id;
      i += 1;
    }
  }

  let source = '';
  for (let k = 0; k < collected.length; k += 1) {
    let text = collected[k] as string;
    const isLast = k === collected.length - 1;
    const hardBreak = trailingBackslashes(text) % 2 === 1;
    if (hardBreak) text = text.slice(0, -1);
    if (isLast && !hardBreak) {
      const split = splitTrailingMarker(text);
      text = split.text;
      id = split.id;
    }
    source += text;
    if (hardBreak) source += HARD_BREAK;
    else if (!isLast) source += ' ';
  }

  return [withId({ type: 'paragraph' }, id, { content: parseInline(source) }), i];
}

/**
 * A fence, or a line that merely starts with backticks.
 *
 * CommonMark forbids a backtick in a fence's info string, and that rule is
 * carrying real weight here: a paragraph beginning with an inline code span
 * (```` ```sh --flag``` ````) opens with three backticks and would otherwise
 * swallow the rest of the document as code.
 */
function openingFence(line: string): { length: number; info: string } | null {
  const match = FENCE.exec(line);
  if (!match) return null;
  const info = match[2] as string;
  if (info.includes('`')) return null;
  return { length: (match[1] as string).length, info };
}

/** Whether a line opens a new block, and so interrupts a paragraph. */
function startsBlock(line: string, next: string | undefined): boolean {
  if (openingFence(line)) return true;
  if (CONTAINER_DIRECTIVE.test(line) || LEAF_DIRECTIVE.test(line)) return true;
  if (HEADING.test(line)) return true;
  if (line.startsWith('>')) return true;
  if (THEMATIC_BREAK.test(line)) return true;
  if (LIST_MARKER.test(line)) return true;
  if (line.startsWith('|') && isDelimiterRow(next)) return true;
  if (readLoneMarker(line)) return true;
  return false;
}

function readCodeBlock(
  lines: string[],
  start: number,
  fenceLength: number,
  info: string,
  indent: number,
): [NodeJSON, number] {
  const { text: languagePart, id } = splitTrailingMarker(info);
  const language = languagePart.trim() || null;

  const body: string[] = [];
  let i = start + 1;
  while (i < lines.length) {
    const raw = lines[i] as string;
    const closing = /^(`{3,})[ \t]*$/.exec(raw.trimStart());
    if (closing && (closing[1] as string).length >= fenceLength) {
      i += 1;
      break;
    }
    body.push(dedent(raw, indent));
    i += 1;
  }

  const text = body.join('\n');
  return [
    withId(
      { type: 'codeBlock', attrs: { language } },
      id,
      text === '' ? {} : { content: [{ type: 'text', text }] },
    ),
    i,
  ];
}

function readContainerDirective(
  lines: string[],
  start: number,
  name: string,
  attrSource: string | undefined,
): [NodeJSON, number] {
  const type = DIRECTIVE_NODES[name];
  if (type !== 'callout') {
    throw new MarkdownParseError(`:::${name} is not one of the components §5.1 defines`);
  }
  const attrs = attrSource ? parseAttributes(attrSource) : { id: null, entries: [] };

  const body: string[] = [];
  let i = start + 1;
  let fenceLength = 0;
  while (i < lines.length) {
    const line = (lines[i] as string).trimStart();
    // A `:::` inside a fenced code block closes nothing — it is code.
    const fence = /^(`{3,})/.exec(line);
    if (fence) {
      const length = (fence[1] as string).length;
      if (fenceLength === 0) fenceLength = length;
      else if (length >= fenceLength) fenceLength = 0;
    }
    if (fenceLength === 0 && line === ':::') {
      i += 1;
      break;
    }
    body.push(lines[i] as string);
    i += 1;
  }

  const variant = attrs.entries.find(([key]) => key === 'variant')?.[1];
  return [
    withId({ type: 'callout', attrs: variant ? { variant } : {} }, attrs.id, {
      content: parseBlocks(body),
    }),
    i,
  ];
}

function readLeafDirective(name: string, attrSource: string | undefined): NodeJSON {
  const type = DIRECTIVE_NODES[name];
  if (type !== 'embed' && type !== 'liveData') {
    throw new MarkdownParseError(`::${name} is not one of the components §5.1 defines`);
  }
  const attrs = attrSource ? parseAttributes(attrSource) : { id: null, entries: [] };
  const lookup = (key: string): string | null =>
    attrs.entries.find(([entryKey]) => entryKey === key)?.[1] || null;

  if (type === 'embed') {
    return withId(
      { type: 'embed', attrs: { url: lookup('url'), title: lookup('title') } },
      attrs.id,
    );
  }

  // Everything outside the reserved names is a parameter — see
  // LIVE_DATA_RESERVED_ATTRIBUTES for why that list can never grow.
  const params: Record<string, string> = {};
  for (const [key, value] of attrs.entries) {
    if (key === 'source' || key === 'format') continue;
    params[key] = value;
  }
  return withId(
    {
      type: 'liveData',
      attrs: { source: lookup('source'), format: lookup('format') ?? 'value', params },
    },
    attrs.id,
  );
}

function readList(lines: string[], start: number): [NodeJSON, number] {
  const first = LIST_MARKER.exec((lines[start] as string).trimStart());
  if (!first) throw new MarkdownParseError('expected a list marker');
  const firstMarker = first[1] as string;
  const baseIndent = (lines[start] as string).length - (lines[start] as string).trimStart().length;

  const items: NodeJSON[] = [];
  let kind: 'bullet' | 'ordered' | 'task' | null = null;
  // Only the first number is read. §5.1 wants one canonical rendering, and a
  // list whose items each remembered their own number would have two.
  const listStart = /\d/.test(firstMarker) ? Number.parseInt(firstMarker, 10) : 1;
  let i = start;

  while (i < lines.length) {
    const raw = lines[i] as string;
    const line = raw.trimStart();
    const indent = raw.length - line.length;
    if (line === '') {
      // A blank line only ends the list if what follows is not another item.
      const next = lines[i + 1];
      if (next === undefined) break;
      const nextTrimmed = next.trimStart();
      const nextIndent = next.length - nextTrimmed.length;
      if (nextTrimmed === '') break;
      if (nextIndent <= baseIndent && !LIST_MARKER.test(nextTrimmed)) break;
      i += 1;
      continue;
    }
    if (indent < baseIndent) break;
    const marker = LIST_MARKER.exec(line);
    if (!marker || indent > baseIndent) break;

    const markerText = marker[0] as string;
    let contentIndent = indent + markerText.length;
    let rest = line.slice(markerText.length);

    const task = TASK_MARKER.exec(rest);
    const itemKind: 'bullet' | 'ordered' | 'task' = task
      ? 'task'
      : /\d/.test(marker[1] as string)
        ? 'ordered'
        : 'bullet';
    if (kind === null) kind = itemKind;
    else if (kind !== itemKind) break;

    let checked = false;
    if (task) {
      checked = (task[1] as string).toLowerCase() === 'x';
      rest = rest.slice((task[0] as string).length);
      // The list marker is `- `; the checkbox belongs to the item's content, so
      // a wrapped task item is indented by the marker alone.
      contentIndent = indent + markerText.length;
    }

    const itemLines = [rest];
    i += 1;
    while (i < lines.length) {
      const candidate = lines[i] as string;
      const trimmed = candidate.trimStart();
      if (trimmed === '') {
        const next = lines[i + 1];
        if (next === undefined) break;
        const nextIndent = next.length - next.trimStart().length;
        if (next.trim() !== '' && nextIndent < contentIndent) break;
        itemLines.push('');
        i += 1;
        continue;
      }
      if (candidate.length - trimmed.length < contentIndent) break;
      itemLines.push(dedent(candidate, contentIndent));
      i += 1;
    }

    const content = ensureParagraph(parseBlocks(itemLines));
    items.push(
      itemKind === 'task'
        ? { type: 'taskItem', attrs: { checked }, content }
        : { type: 'listItem', content },
    );
  }

  if (kind === 'task') return [{ type: 'taskList', content: items }, i];
  if (kind === 'ordered') {
    return [{ type: 'orderedList', attrs: { start: listStart }, content: items }, i];
  }
  return [{ type: 'bulletList', content: items }, i];
}

/** `listItem` and `taskItem` both require a leading paragraph. */
function ensureParagraph(content: NodeJSON[]): NodeJSON[] {
  if (content.length === 0 || content[0]?.type !== 'paragraph') {
    return [{ type: 'paragraph' }, ...content];
  }
  return content;
}

function readTable(lines: string[], start: number): [NodeJSON, number] {
  const header = splitRow(lines[start] as string);
  let i = start + 2;
  const rows: NodeJSON[] = [
    {
      type: 'tableRow',
      content: header.map((cell) => ({ type: 'tableHeader', content: parseInline(cell, true) })),
    },
  ];

  while (i < lines.length) {
    const line = (lines[i] as string).trimStart();
    if (!line.startsWith('|')) break;
    rows.push({
      type: 'tableRow',
      content: splitRow(line).map((cell) => ({
        type: 'tableCell',
        content: parseInline(cell, true),
      })),
    });
    i += 1;
  }

  let id: string | null = null;
  if (i < lines.length) {
    const marker = readLoneMarker((lines[i] as string).trimStart());
    if (marker) {
      id = marker.id;
      i += 1;
    }
  }

  return [withId({ type: 'table' }, id, { content: rows }), i];
}

function isDelimiterRow(line: string | undefined): boolean {
  if (line === undefined) return false;
  const trimmed = line.trim();
  if (!trimmed.startsWith('|')) return false;
  const cells = splitRow(trimmed);
  return cells.length > 0 && cells.every((cell) => /^:?-+:?$/.test(cell));
}

function splitRow(line: string): string[] {
  const source = line.trim();
  const cells: string[] = [];
  let current = '';
  let i = source.startsWith('|') ? 1 : 0;
  for (; i < source.length; i += 1) {
    const char = source[i] as string;
    if (char === '\\' && i + 1 < source.length) {
      current += char + (source[i + 1] as string);
      i += 1;
      continue;
    }
    if (char === '|') {
      cells.push(current);
      current = '';
      continue;
    }
    current += char;
  }
  if (current.trim() !== '') cells.push(current);
  return cells.map(trimCell);
}

/**
 * Strips a cell's padding without eating text.
 *
 * A cell whose text really ends in a space is written `a\ `, and a plain trim
 * would take the space and leave the backslash dangling — turning "a " into
 * "a\". Trailing whitespace an odd number of backslashes protect is content.
 */
function trimCell(cell: string): string {
  const withoutLeading = cell.replace(/^[ \t]+/, '');
  const trailing = /[ \t]+$/.exec(withoutLeading);
  if (!trailing) return withoutLeading;
  const before = withoutLeading.slice(0, trailing.index);
  return trailingBackslashes(before) % 2 === 1 ? `${before}${trailing[0][0] as string}` : before;
}

// ---------------------------------------------------------------------------
// Inline
// ---------------------------------------------------------------------------

function parseInline(source: string, inTableCell = false, marks: MarkJSON[] = []): NodeJSON[] {
  const out: NodeJSON[] = [];
  let buffer = '';

  const flush = (): void => {
    if (buffer === '') return;
    out.push(
      marks.length > 0 ? { type: 'text', text: buffer, marks } : { type: 'text', text: buffer },
    );
    buffer = '';
  };

  let i = 0;
  while (i < source.length) {
    const char = source[i] as string;

    if (char === '\\' && i + 1 < source.length) {
      buffer += source[i + 1] as string;
      i += 2;
      continue;
    }

    if (char === HARD_BREAK) {
      flush();
      out.push({ type: 'hardBreak' });
      i += 1;
      continue;
    }

    if (char === '`') {
      const span = readCodeSpan(source, i, inTableCell);
      if (span) {
        flush();
        out.push({ type: 'text', text: span.text, marks: [...marks, { type: 'code' }] });
        i = span.next;
        continue;
      }
    }

    if (char === '[') {
      const link = readLink(source, i);
      if (link) {
        flush();
        out.push(...parseInline(link.text, inTableCell, [...marks, link.mark]));
        i = link.next;
        continue;
      }
    }

    const delimiter = openingDelimiter(source, i);
    if (delimiter) {
      const width = delimiter.token.length;
      const closer = findCloser(source, i + width, delimiter.token, inTableCell);
      if (closer >= 0) {
        flush();
        const applied = delimiter.marks.map((type) => ({ type }));
        out.push(
          ...parseInline(source.slice(i + width, closer), inTableCell, [...marks, ...applied]),
        );
        i = closer + width;
        continue;
      }
    }

    buffer += char;
    i += 1;
  }

  flush();
  return out;
}

/**
 * Canonical markdown uses `**` for bold and `_` for italic, which is why this
 * needs no flanking rules — every literal `*` and `_` arrives escaped. The
 * asterisk spellings are here for tolerance only: an agent writing a draft back
 * (§11.3) will reach for `*italic*` and `***both***`, and reading those is free.
 */
function openingDelimiter(source: string, i: number): { token: string; marks: string[] } | null {
  if (source.startsWith('***', i)) return { token: '***', marks: ['bold', 'italic'] };
  if (source.startsWith('**', i)) return { token: '**', marks: ['bold'] };
  if (source.startsWith('~~', i)) return { token: '~~', marks: ['strike'] };
  if (source.startsWith('___', i)) return { token: '___', marks: ['bold', 'italic'] };
  if (source.startsWith('__', i)) return { token: '__', marks: ['bold'] };
  if (source[i] === '*') return { token: '*', marks: ['italic'] };
  if (source[i] === '_') return { token: '_', marks: ['italic'] };
  return null;
}

/**
 * Finds the matching closer, stepping over escapes, code spans, and whole
 * links. Stepping over a link matters: a URL is not escaped, so a `*` inside
 * one would otherwise close emphasis that opened outside it.
 */
function findCloser(source: string, from: number, token: string, inTableCell: boolean): number {
  let i = from;
  while (i < source.length) {
    const char = source[i] as string;
    if (char === '\\') {
      i += 2;
      continue;
    }
    if (char === '`') {
      const span = readCodeSpan(source, i, inTableCell);
      if (span) {
        i = span.next;
        continue;
      }
    }
    if (char === '[') {
      const link = readLink(source, i);
      if (link) {
        i = link.next;
        continue;
      }
    }
    if (source.startsWith(token, i)) return i;
    // A doubled character met while looking for a single one is a nested bold
    // delimiter, not this run's closer.
    if (token.length === 1 && source.startsWith(token + token, i)) {
      i += 2;
      continue;
    }
    i += 1;
  }
  return -1;
}

function readCodeSpan(
  source: string,
  i: number,
  inTableCell: boolean,
): { text: string; next: number } | null {
  const open = /^`+/.exec(source.slice(i));
  if (!open) return null;
  const fence = open[0] as string;
  let search = i + fence.length;
  while (search < source.length) {
    if (source[search] === '`') {
      const run = /^`+/.exec(source.slice(search)) as RegExpExecArray;
      if ((run[0] as string).length === fence.length) {
        let text = source.slice(i + fence.length, search);
        // CommonMark's padding rule, and the inverse of what the serializer
        // does when the code itself starts or ends with a backtick or a space.
        if (text.startsWith(' ') && text.endsWith(' ') && text.trim() !== '') {
          text = text.slice(1, -1);
        }
        // Inside a cell the pipe and the backslash before it are the only two
        // characters a code span escapes; see `serializeCodeSpan`.
        if (inTableCell) text = text.replace(/\\([\\|])/g, '$1');
        return text === '' ? null : { text, next: search + fence.length };
      }
      search += (run[0] as string).length;
      continue;
    }
    search += 1;
  }
  return null;
}

function readLink(
  source: string,
  i: number,
): { text: string; mark: MarkJSON; next: number } | null {
  let depth = 0;
  let close = -1;
  for (let k = i; k < source.length; k += 1) {
    const char = source[k] as string;
    if (char === '\\') {
      k += 1;
      continue;
    }
    if (char === '[') depth += 1;
    else if (char === ']') {
      depth -= 1;
      if (depth === 0) {
        close = k;
        break;
      }
    }
  }
  if (close < 0 || source[close + 1] !== '(') return null;

  const destination = readLinkDestination(source, close + 2);
  if (!destination) return null;

  return {
    text: source.slice(i + 1, close),
    mark: {
      type: 'link',
      attrs: { href: destination.href || null, title: destination.title },
    },
    next: destination.next,
  };
}

function readLinkDestination(
  source: string,
  start: number,
): { href: string; title: string | null; next: number } | null {
  let i = start;
  while (source[i] === ' ' || source[i] === '\t') i += 1;

  let href = '';
  if (source[i] === '<') {
    i += 1;
    while (i < source.length && source[i] !== '>') {
      if (source[i] === '\\' && i + 1 < source.length) {
        href += source[i + 1] as string;
        i += 2;
        continue;
      }
      href += source[i] as string;
      i += 1;
    }
    if (source[i] !== '>') return null;
    i += 1;
  } else {
    let depth = 0;
    while (i < source.length) {
      const char = source[i] as string;
      if (char === '\\' && i + 1 < source.length) {
        href += source[i + 1] as string;
        i += 2;
        continue;
      }
      if (char === ' ' || char === '\t') break;
      if (char === '(') depth += 1;
      if (char === ')') {
        if (depth === 0) break;
        depth -= 1;
      }
      href += char;
      i += 1;
    }
  }

  while (source[i] === ' ' || source[i] === '\t') i += 1;

  let title: string | null = null;
  if (source[i] === '"') {
    i += 1;
    let value = '';
    while (i < source.length && source[i] !== '"') {
      if (source[i] === '\\' && i + 1 < source.length) {
        value += source[i + 1] as string;
        i += 2;
        continue;
      }
      value += source[i] as string;
      i += 1;
    }
    if (source[i] !== '"') return null;
    title = value;
    i += 1;
  }

  while (source[i] === ' ' || source[i] === '\t') i += 1;
  if (source[i] !== ')') return null;
  return { href, title, next: i + 1 };
}

// ---------------------------------------------------------------------------
// Small helpers
// ---------------------------------------------------------------------------

function withId(node: NodeJSON, id: string | null, extra: Partial<NodeJSON> = {}): NodeJSON {
  const attrs = { ...(node.attrs ?? {}), ...(id ? { id } : {}) };
  const result: NodeJSON = { ...node, ...extra };
  if (Object.keys(attrs).length > 0) result.attrs = attrs;
  return result;
}

function dedent(line: string, amount: number): string {
  let i = 0;
  while (i < amount && (line[i] === ' ' || line[i] === '\t')) i += 1;
  return line.slice(i);
}

function trailingBackslashes(line: string): number {
  let count = 0;
  for (let i = line.length - 1; i >= 0 && line[i] === '\\'; i -= 1) count += 1;
  return count;
}

function unescapeText(text: string): string {
  let out = '';
  for (let i = 0; i < text.length; i += 1) {
    if (text[i] === '\\' && i + 1 < text.length) {
      out += text[i + 1] as string;
      i += 1;
      continue;
    }
    out += text[i] as string;
  }
  return out;
}

function readDestination(raw: string): string {
  if (raw.startsWith('<') && raw.endsWith('>')) return unescapeText(raw.slice(1, -1));
  return unescapeText(raw);
}
